<template>
  <!-- Auth entry surface: one no-scroll arena column. Masthead + card centered as
       a single unit; on the landing the legal nav is pinned to its own bottom row. -->
  <div v-if="!isContentPage" class="au-col safe-area-insets">
    <div class="au-center">
      <!-- Landing -->
      <template v-if="currentState === 'homepage'">
        <AuthBrand variant="hero" />

        <div class="au-card">
          <p class="au-flavor">{{ STRINGS.auth.flavorText }}</p>
          <div class="au-divider"></div>
          <div class="au-actions">
            <button
              type="button"
              class="au-btn au-btn--primary"
              :aria-label="STRINGS.aria.labels.logIn"
              data-testid="homepage-login-button"
              @click="navigateTo('login')"
            >
              {{ STRINGS.auth.buttons.logIn }}
            </button>
            <button
              type="button"
              class="au-btn au-btn--secondary"
              :aria-label="STRINGS.aria.labels.createAccount"
              data-testid="homepage-signup-button"
              @click="navigateTo('signup')"
            >
              {{ STRINGS.auth.buttons.createAccount }}
            </button>
          </div>
        </div>
      </template>

      <!-- Form states -->
      <template v-else>
        <AuthBrand variant="compact" :title="compactTitle" />

        <AuthLoginForm v-if="currentState === 'login'" @navigate="handleAuthNavigate" />

        <AuthSignupForm
          v-else-if="currentState === 'signup'"
          :invite-token="props.inviteToken ?? undefined"
          @success="handleSignupSuccess"
          @navigate="handleAuthNavigate"
        />

        <AuthForgotPasswordForm
          v-else-if="currentState === 'forgot-password'"
          @navigate="handleAuthNavigate"
        />

        <template v-else-if="currentState === 'reset-password'">
          <AuthResetPasswordForm
            v-if="resetToken"
            :token="resetToken"
            @navigate="handleAuthNavigate"
          />
          <AuthForgotPasswordForm v-else @navigate="handleAuthNavigate" />
        </template>
      </template>
    </div>

    <!-- Legal nav, landing only — pinned bottom row -->
    <FooterLegal v-if="currentState === 'homepage'" @navigate="handleAuthNavigate" />
  </div>

  <!-- Content pages own their scroll region: the app root (#app) is overflow:hidden,
       so this wrapper is bounded to the viewport and scrolls internally — mirroring
       the .au-col → .au-center model above. overflow-x is pinned hidden because
       overflow-y:auto alone promotes overflow-x to auto (CSS Overflow §3), which
       would re-allow sideways panning. -->
  <div
    v-else
    class="flex flex-col h-[100dvh] overflow-y-auto overflow-x-hidden w-full mx-auto safe-area-insets"
  >
    <section class="flex-1 flex flex-col p-4 md:p-6 lg:p-8">
      <ContentPage
        :page="currentState as 'about' | 'privacy' | 'terms'"
        @navigate="handleAuthNavigate"
      />
    </section>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, onMounted, onUnmounted } from 'vue';
import AuthBrand from './AuthBrand.vue';
import AuthLoginForm from './AuthLoginForm.vue';
import AuthSignupForm from './AuthSignupForm.vue';
import AuthForgotPasswordForm from './AuthForgotPasswordForm.vue';
import AuthResetPasswordForm from './AuthResetPasswordForm.vue';
import ContentPage from './ContentPage.vue';
import FooterLegal from './FooterLegal.vue';
import type { MatchmakingScreenState, NavigationState } from '../types/websocket';
import STRINGS from '@/constants/strings';

const props = defineProps<{ inviteToken?: string | null }>();

const currentState = ref<MatchmakingScreenState>('homepage');
const resetToken = ref<string | null>(null);

const VALID_NAV_STATES: readonly MatchmakingScreenState[] = [
  'homepage',
  'login',
  'signup',
  'forgot-password',
  'reset-password',
  'about',
  'privacy',
  'terms',
];

const isContentPage = computed(
  () =>
    currentState.value === 'about' ||
    currentState.value === 'privacy' ||
    currentState.value === 'terms'
);

// Title shown in the compact brand masthead for each form state. The brand
// uppercases via CSS; these strings are already uppercase in STRINGS.
const compactTitle = computed(() => {
  switch (currentState.value) {
    case 'login':
      return STRINGS.auth.buttons.logIn;
    case 'signup':
      return STRINGS.auth.buttons.createAccount;
    case 'forgot-password':
      return STRINGS.auth.passwordReset.heading;
    case 'reset-password':
      return resetToken.value
        ? STRINGS.auth.passwordReset.resetFormHeading
        : STRINGS.auth.passwordReset.heading;
    default:
      return '';
  }
});

// Push a history entry each time we change auth sub-view so the browser
// Back button returns to the previous sub-view instead of exiting the SPA.
const navigateTo = (target: MatchmakingScreenState) => {
  if (currentState.value === target) return;
  window.history.pushState({ authState: target }, '');
  currentState.value = target;
};

const handleSignupSuccess = (_name: string) => {
  navigateTo('homepage');
};

const handleAuthNavigate = (target: NavigationState) => {
  navigateTo(target);
};

const handlePopState = (event: PopStateEvent) => {
  const restored = event.state?.authState;
  if (typeof restored === 'string' && (VALID_NAV_STATES as readonly string[]).includes(restored)) {
    currentState.value = restored as MatchmakingScreenState;
  }
};

onMounted(() => {
  let initial: MatchmakingScreenState = 'homepage';
  let path = window.location.pathname;

  if (props.inviteToken || path === '/signup') {
    initial = 'signup';
  }

  const params = new URLSearchParams(window.location.search);
  const token = params.get('reset_token');
  if (token) {
    resetToken.value = token;
    initial = 'reset-password';
    path = window.location.pathname;
    window.history.replaceState({ authState: initial }, '', path);
  } else {
    window.history.replaceState({ authState: initial }, '');
  }

  currentState.value = initial;
  window.addEventListener('popstate', handlePopState);
});

onUnmounted(() => {
  window.removeEventListener('popstate', handlePopState);
});
</script>
